import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import { useTheme } from "styled-components";

import {
  bookmarkStrategy,
  unbookmarkStrategy,
} from "features/Strategy/service";

import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { trackEvent } from "utils/tracking";
import { Button } from "components/Button";
import { useNavigation } from "utils/navigate";
import StrategyControlModal from "shared/StrategyControl";
import { BookmarkIconV2, ShareIconV2, EditPenIcon } from "assets/icons";
import { NO_CODE_BUILDER_V2_EDIT_PATH } from "features/Strategy/routes";
import { DetailsHeaderContainer } from "features/Strategy/v2/Detail/StrategyDetails.styles";
import { StrategyShareModal } from "shared/Modal/StrategyShareModal/index";
import { useCommunityStrategies } from "features/Strategy/hook/useCommunityStrategies";

const DetailsHeader = ({
  strategyDetail,
  showBookmarkIcon,
  showShareIcon = true,
  isLoading,
  showEditIcon,
  isPublicStrategy,
  fromSignup = false,
  onClose = () => {},
}) => {
  const theme = useTheme();

  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    navigateToV2StrategyEdit,
    navigateToV2NoCodeBuilderEdit,
    navigateToV2EtfBuilder,
  } = useNavigation();
  const { refetch: refetchStrategies } = useCommunityStrategies({
    type: "bookmarked",
    enabled: false,
  });

  const [showShareModal, setShowShareModal] = useState(false);
  const [bookmark, setBookmark] = useState(strategyDetail.bookmarked);
  const [showStrategyControlModal, setShowStrategyControlModal] =
    useState(false);

  useEffect(() => {
    setBookmark(strategyDetail.bookmarked);
  }, [strategyDetail]);

  const toggleShareModal = () => {
    setShowShareModal(!showShareModal);
    trackEvent({
      category: "strategy_share",
      label: "Strategy",
    });
  };

  const toggleBookmark = async () => {
    if (isLoading || isPublicStrategy) {
      return;
    }

    setBookmark(!bookmark);

    try {
      let trackEventName = "strategy_bookmark";

      if (bookmark) {
        trackEventName = "strategy_unbookmark";
        await unbookmarkStrategy(id);
      } else {
        await bookmarkStrategy(id);
        dispatch(
          toast({
            body: "Strategy bookmarked",
          })
        );
      }

      refetchStrategies();

      trackEvent({
        action: "submit",
        category: trackEventName,
        label: "Strategy",
      });
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    }
  };

  const toggleStrategyControlModal = async () => {
    setShowStrategyControlModal(!showStrategyControlModal);
  };

  const handleEdit = () => {
    if (!strategyDetail.can_edit) {
      return;
    }

    const isNoCodeStrategy = strategyDetail.strategy_type === "nocode";

    const isEtfStrategy = strategyDetail.strategy_type === "etf";
    const strategyId = strategyDetail.strategy_id;

    if (isNoCodeStrategy) {
      const options = {
        pathname: NO_CODE_BUILDER_V2_EDIT_PATH.replace(":id", strategyId),
        search: `?activeTab=overview`,
      };
      navigateToV2NoCodeBuilderEdit(options);
    } else if (isEtfStrategy) {
      navigateToV2EtfBuilder(strategyId);
    } else {
      navigateToV2StrategyEdit(strategyId);
    }

    trackEvent({
      action: "submit",
      category: "strategy_edit",
      label: "Strategy",
    });
  };

  const handleInvest = async () => {
    if (fromSignup) {
      onClose();

      return;
    }

    toggleStrategyControlModal();
  };

  return (
    <DetailsHeaderContainer className="chek">
      <div className="heading-wrapper">
        <div className="position-relative sub-header w-100">
          <div className="action-wrapper w-100">
            <div
              className={classNames("wt-btn-invest", {
                "w-100": fromSignup,
              })}
            >
              <Button
                className={classNames("btn-invest", {
                  "disable-btn": isLoading || isPublicStrategy,
                })}
                onClick={handleInvest}
                disabled={isLoading || isPublicStrategy}
              >
                {!fromSignup ? "Invest" : "Close"}
              </Button>
            </div>

            {showEditIcon && (
              <Button
                className={classNames("btn-edit", {
                  "disable-btn": !strategyDetail.can_edit || isLoading,
                })}
                onClick={handleEdit}
                disabled={!strategyDetail.can_edit || isLoading}
              >
                <EditPenIcon />
              </Button>
            )}

            {showBookmarkIcon && (
              <div
                className={classNames("icon-button", {
                  "disable-btn": !bookmark,
                  disabled: isLoading || isPublicStrategy,
                })}
                onClick={toggleBookmark}
              >
                <BookmarkIconV2 fill={theme.button.font.senary} />
              </div>
            )}

            {showShareIcon && (
              <div onClick={toggleShareModal} className="icon-button">
                <span className="app-tooltip">
                  <ShareIconV2 fill={theme.button.font.senary} />
                  {/* <span className="tooltiptext bottom">Coming soon</span> */}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showShareModal && (
        <StrategyShareModal
          showShareModal={showShareModal}
          toggleShareModal={toggleShareModal}
          strategyDetail={strategyDetail}
        />
      )}

      <StrategyControlModal
        show={showStrategyControlModal}
        onHide={toggleStrategyControlModal}
        selectedStrategyData={strategyDetail}
      />
    </DetailsHeaderContainer>
  );
};

export default DetailsHeader;

DetailsHeader.propTypes = {
  strategyDetail: PropTypes.object,
  showBookmarkIcon: PropTypes.bool,
  showShareIcon: PropTypes.bool,
  isLoading: PropTypes.bool,
  showEditIcon: PropTypes.bool,
  isPublicStrategy: PropTypes.bool,
  fromSignup: PropTypes.bool,
  onClose: PropTypes.func,
};
